import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Button } from '../../components/Button';
import { Card, Page } from '../../components/Page';
import { StatusBadge, type Tone } from '../../components/StatusBadge';
import { useDb } from '../../db/hooks';
import { useProfile } from '../../data/profile';
import { useSchedules } from '../../data/schedule';
import { deleteSession, useSession, useSessions } from '../../data/sessions';
import { useCanWrite } from '../../account/useCanWrite';
import { formatKg, interdialyticGains, STATUS_LABELS, weightRemoved, type SessionStatus } from '../../lib/sessions';
import { plannedSessions, scheduleOn } from '../../lib/schedule';
import { AfterStep, BeforeStep, DuringStep, type BeforeDefaults } from './SessionSteps';
import { localToday } from './fields';

type Step = 'before' | 'during' | 'after';
const STEPS: { id: Step; label: string }[] = [
  { id: 'before', label: 'Before' },
  { id: 'during', label: 'During' },
  { id: 'after', label: 'After' }
];

export const STATUS_TONES: Record<SessionStatus, Tone> = {
  in_progress: 'blue',
  completed: 'good',
  missed: 'danger',
  ended_early: 'warn'
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-clinical-muted">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

/** Log or edit a session: /sessions/new?date=… or /sessions/:id?step=… (SPEC.md §5). */
export function SessionPage() {
  const { id } = useParams();
  const isNew = !id;
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const db = useDb();
  const canWrite = useCanWrite();
  const { session, vitals, isLoading } = useSession(id);
  const { sessions } = useSessions();
  const { profile, isLoading: profileLoading } = useProfile();
  const { schedules, exceptions, isLoading: schedulesLoading } = useSchedules();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const step: Step = isNew ? 'before' : ((params.get('step') as Step | null) ?? (session?.status === 'in_progress' ? 'during' : 'before'));
  const date = params.get('date') ?? localToday();

  const defaults: BeforeDefaults = useMemo(() => {
    const day = parseISO(date);
    const planned = plannedSessions(schedules, exceptions, day, day)[0];
    return {
      session_date: date,
      modality: scheduleOn(schedules, date)?.modality ?? profile?.modality ?? 'in_center',
      start_time: planned?.time ?? null,
      dry_weight_kg: profile?.dry_weight_kg ?? null
    };
  }, [date, schedules, exceptions, profile]);

  if (!isNew && !isLoading && !session) return <Navigate to="/sessions" replace />;
  if (!isNew && (isLoading || !session)) return null;
  // New-session defaults come from the profile and schedule; wait for them before mounting the form.
  if (isNew && (profileLoading || schedulesLoading)) return null;
  if (!canWrite && isNew) return <Navigate to="/sessions" replace />;

  const pre = vitals.find((vital) => vital.phase === 'pre');
  const post = vitals.find((vital) => vital.phase === 'post');
  const intra = vitals.filter((vital) => vital.phase === 'intra');
  const gain = session ? interdialyticGains(sessions).get(session.id) : undefined;
  const removed = session ? weightRemoved(session) : null;
  const aboveDry = session?.pre_weight_kg != null && session.dry_weight_kg != null ? session.pre_weight_kg - session.dry_weight_kg : null;
  const goTo = (next: Step) => setParams({ step: next }, { replace: true });

  const title = session ? format(parseISO(session.session_date), 'EEEE d MMMM') : 'Log a session';

  if (session?.status === 'missed') {
    return (
      <Page title={title} back="/sessions">
        <Card>
          <StatusBadge tone="danger">Missed</StatusBadge>
          {session.missed_reason ? <p className="mt-2 text-sm">{session.missed_reason}</p> : null}
        </Card>
        {canWrite ? (
          <DeleteSection confirm={confirmDelete} setConfirm={setConfirmDelete} onDelete={() => void deleteSession(db, session.id, vitals).then(() => navigate('/sessions'))} />
        ) : null}
      </Page>
    );
  }

  return (
    <Page title={title} back="/sessions">
      {session ? (
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <StatusBadge tone={STATUS_TONES[session.status]}>{STATUS_LABELS[session.status]}</StatusBadge>
            {session.start_time ? (
              <span className="text-sm text-clinical-muted">
                {session.start_time}
                {session.end_time ? `–${session.end_time}` : ''}
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Removed" value={formatKg(removed)} />
            <Stat label="Gain since last" value={gain == null ? '–' : formatKg(gain, true)} />
            <Stat label="Above dry weight" value={aboveDry == null ? '–' : formatKg(aboveDry, true)} />
          </div>
        </Card>
      ) : null}

      <nav aria-label="Session steps" className="grid grid-cols-3 gap-1 rounded-xl bg-clinical-line/40 p-1">
        {STEPS.map((item) => (
          <button
            key={item.id}
            type="button"
            disabled={isNew && item.id !== 'before'}
            aria-current={step === item.id ? 'step' : undefined}
            onClick={() => goTo(item.id)}
            className={`focus-ring min-h-10 rounded-lg text-sm font-semibold transition disabled:opacity-40 ${
              step === item.id ? 'bg-clinical-card text-clinical-ink shadow-sm' : 'text-clinical-muted'
            }`}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <Card>
        <fieldset disabled={!canWrite}>
          {step === 'before' ? (
            <BeforeStep
              key={session?.id ?? 'new'}
              session={session}
              vitals={pre}
              defaults={defaults}
              onSaved={(savedId) => navigate(`/sessions/${savedId}?step=during`, { replace: true })}
            />
          ) : step === 'during' && session ? (
            <DuringStep session={session} readings={intra} onNext={() => goTo('after')} />
          ) : session ? (
            <AfterStep key={session.id} session={session} vitals={post} onSaved={() => navigate('/sessions')} />
          ) : null}
        </fieldset>
      </Card>

      {session && canWrite ? (
        <DeleteSection confirm={confirmDelete} setConfirm={setConfirmDelete} onDelete={() => void deleteSession(db, session.id, vitals).then(() => navigate('/sessions'))} />
      ) : null}
    </Page>
  );
}

function DeleteSection({ confirm, setConfirm, onDelete }: { confirm: boolean; setConfirm: (value: boolean) => void; onDelete: () => void }) {
  return confirm ? (
    <div className="flex items-center gap-2">
      <span className="flex-1 text-sm">Delete this session and its readings?</span>
      <Button variant="danger" onClick={onDelete}>
        Delete
      </Button>
      <Button variant="secondary" onClick={() => setConfirm(false)}>
        Cancel
      </Button>
    </div>
  ) : (
    <Button variant="ghost" className="w-full !text-clinical-danger hover:!bg-red-500/10" onClick={() => setConfirm(true)}>
      Delete session
    </Button>
  );
}
